// UltraFlex — Site selection & permissions context.
// Top-level site filter for the admin UI. User's selected site (or "all") is
// stored in localStorage and propagated via React context so every page can
// filter its data by the active site.
//
// Also installs a `window.fetch` interceptor once, so every API call made by
// the app automatically includes `X-Active-Site: <siteId|all>` — this is the
// signal the backend uses (via `get_site_scope`) to narrow queries.
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'uf_active_site_id';

const SiteContext = createContext(null);

// --- Global fetch interceptor --------------------------------------------
// Installs once per page load. Reads the current active site from localStorage
// (synchronously accessible from any fetch call) and appends it as a header.
let _originalFetch = null;
const installFetchInterceptor = () => {
  if (_originalFetch) return;
  _originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    try {
      // Only inject for same-backend API calls
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isApi = url.startsWith(API) && url.includes('/api/');
      if (isApi) {
        const activeSite = localStorage.getItem(STORAGE_KEY);
        const headers = new Headers(init.headers || (typeof input === 'object' && input.headers) || {});
        if (activeSite && !headers.has('X-Active-Site')) {
          headers.set('X-Active-Site', activeSite);
        }
        return _originalFetch(input, { ...init, headers });
      }
    } catch (e) {
      // Never break the call due to header injection — fall through.
    }
    return _originalFetch(input, init);
  };
};

export function SiteProvider({ children }) {
  const { token, isAuthenticated, isStaff } = useAuth();
  const [allowedSites, setAllowedSites] = useState([]);   // full Site objects
  const [hasAllSitesAccess, setHasAllSitesAccess] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [roleEnum, setRoleEnum] = useState(null);
  // Initialise from localStorage so the value survives reloads even before
  // /api/permissions/me/effective resolves. null = "All sites".
  const [activeSiteId, setActiveSiteId] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored || stored === 'all') return null;
      return stored;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  // Sticky flag: once we've successfully fetched the user's permissions even
  // ONCE, we keep it true. ProtectedRoute can trust this across SPA route
  // changes — it prevents a transient refresh() call from bouncing the user
  // back to /staff-portal mid-navigation.
  const [hasResolvedOnce, setHasResolvedOnce] = useState(false);
  const interceptorInstalled = useRef(false);

  // Install fetch interceptor once on mount
  useEffect(() => {
    if (!interceptorInstalled.current) {
      installFetchInterceptor();
      interceptorInstalled.current = true;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !isStaff?.()) {
      setAllowedSites([]); setHasAllSitesAccess(false); setPermissions([]); setRoleEnum(null); setLoading(false);
      // Do NOT reset hasResolvedOnce — once true, stays true until logout/page reload.
      return;
    }
    setLoading(true);
    try {
      const [meRes, sitesRes] = await Promise.all([
        fetch(`${API}/api/permissions/me/effective`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sites`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const me = meRes.ok ? await meRes.json() : { permissions: [], allowed_sites: [], all_sites: false, role_enum: null };
      const sitesData = sitesRes.ok ? await sitesRes.json() : [];
      const allSites = (sitesData.items || sitesData || []).filter(s => s.site_type !== 'warehouse');

      setPermissions(me.permissions || []);
      setHasAllSitesAccess(!!me.all_sites);
      setRoleEnum(me.role_enum || null);
      setHasResolvedOnce(true);  // sticky — gate by this in route guards

      let visible;
      if (me.all_sites) {
        visible = allSites;
      } else if (me.allowed_sites?.length) {
        visible = allSites.filter(s => me.allowed_sites.includes(s.id));
      } else {
        visible = [];
      }
      setAllowedSites(visible);

      // Restore persisted site; validate it's still allowed.
      // Important: only override the existing activeSiteId if the stored
      // value is no longer valid for this user — otherwise we'd reset their
      // pick on every refresh() call.
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && stored !== 'all' && visible.find(s => s.id === stored)) {
        // Stored site is still valid → keep it active.
        setActiveSiteId(stored);
      } else if (stored === 'all' && me.all_sites) {
        setActiveSiteId(null);
      } else if (me.all_sites) {
        // No valid stored site, but user has all-sites access → "All Sites".
        setActiveSiteId(null);
      } else if (visible.length) {
        // Single-site (or no-stored) user → land on first allowed site.
        setActiveSiteId(visible[0].id);
      } else {
        setActiveSiteId(null);
      }
    } finally {
      setLoading(false);
    }
    // Deliberately omit `isStaff` from deps — it's a function from AuthContext
    // whose identity may change on every render. We only want to re-fetch when
    // the actual auth state (token / isAuthenticated) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAuthenticated]);

  useEffect(() => { refresh(); }, [refresh]);

  // Keep localStorage in sync with activeSiteId so the fetch interceptor
  // always sends the right header.
  useEffect(() => {
    if (loading) return;
    if (activeSiteId === null && hasAllSitesAccess) {
      localStorage.setItem(STORAGE_KEY, 'all');
    } else if (activeSiteId) {
      localStorage.setItem(STORAGE_KEY, activeSiteId);
    }
  }, [activeSiteId, hasAllSitesAccess, loading]);

  const selectSite = useCallback((siteId) => {
    setActiveSiteId(siteId);
    localStorage.setItem(STORAGE_KEY, siteId === null ? 'all' : siteId);
  }, []);

  const hasPermission = useCallback((perm) => {
    if (!perm) return true;
    // Super/developer admins always pass (server mirrors this)
    if (roleEnum === 'super_admin' || roleEnum === 'developer_admin') return true;
    return permissions.includes(perm);
  }, [permissions, roleEnum]);

  const hasAnyPermission = useCallback((perms) => {
    if (!perms || perms.length === 0) return true;
    if (roleEnum === 'super_admin' || roleEnum === 'developer_admin') return true;
    return perms.some(p => permissions.includes(p));
  }, [permissions, roleEnum]);

  return (
    <SiteContext.Provider value={{
      activeSiteId,          // null = "All", or specific site id
      allowedSites,          // list of Site objects the user can pick
      hasAllSitesAccess,     // true → "All Sites" option is available
      permissions,
      roleEnum,
      loading,
      hasResolvedOnce,       // sticky: true once /api/permissions/me/effective resolved
      selectSite,
      hasPermission,
      hasAnyPermission,
      refresh,
    }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSite must be used within SiteProvider');
  return ctx;
}

// Convenience hook for gating individual buttons / sections.
export function usePermission(perm) {
  const { hasPermission, loading } = useSite();
  return { allowed: hasPermission(perm), loading };
}
